/**
 * 画布大小对话框（参照 Photoshop「图像→画布大小」）。
 * 宽高 + 3×3 锚点九宫格：只改边界，内容按锚点对齐，不缩放内容。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { CanvasAnchor } from '../../app/canvas-size';
import styles from './CanvasSizeDialog.module.css';

const ANCHOR_GRID: readonly CanvasAnchor[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'center',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

export interface CanvasSizeDialogProps {
  readonly open: boolean;
  readonly currentWidth: number;
  readonly currentHeight: number;
  readonly onClose: () => void;
  readonly onApply: (width: number, height: number, anchor: CanvasAnchor) => void;
}

export function CanvasSizeDialog(props: CanvasSizeDialogProps): JSX.Element {
  const [width, setWidth] = createSignal(props.currentWidth);
  const [height, setHeight] = createSignal(props.currentHeight);
  const [anchor, setAnchor] = createSignal<CanvasAnchor>('center');
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 组件实例贯穿应用生命周期，每次打开都用当前文档尺寸重置
  createEffect(() => {
    if (props.open) {
      setWidth(props.currentWidth);
      setHeight(props.currentHeight);
      setAnchor('center');
    }
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="canvas-size-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="画布大小"
          data-testid="canvas-size-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>画布大小</h2>

          <label class={styles.row}>
            <span class={styles.label}>宽度</span>
            <input
              type="number"
              min="1"
              value={width()}
              data-testid="canvas-size-width"
              onInput={(e) => setWidth(Number(e.currentTarget.value) || 1)}
            />
          </label>
          <label class={styles.row}>
            <span class={styles.label}>高度</span>
            <input
              type="number"
              min="1"
              value={height()}
              data-testid="canvas-size-height"
              onInput={(e) => setHeight(Number(e.currentTarget.value) || 1)}
            />
          </label>

          <div class={styles.anchorGrid} data-testid="canvas-size-anchor-grid">
            <For each={ANCHOR_GRID}>
              {(a) => (
                <button
                  type="button"
                  class={styles.anchorCell}
                  classList={{ [styles.anchorCellActive!]: anchor() === a }}
                  aria-label={a}
                  data-testid={`canvas-size-anchor-${a}`}
                  onClick={() => setAnchor(a)}
                />
              )}
            </For>
          </div>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="canvas-size-confirm"
              onClick={() => props.onApply(width(), height(), anchor())}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
